import { Icon20More } from '@vkontakte/icons';
import type { LottieRefCurrentProps } from 'lottie-react';
import React, { type FC, useEffect, useMemo, useRef } from 'react';
import { AnimatedScale } from 'reactApp/ui/AnimatedScale/AnimatedScale';
import { DropdownItemAction } from 'reactApp/ui/DropdownItemAction/DropdownItemAction';
import { DropdownList, DropdownTheme } from 'reactApp/ui/DropdownList/DropdownList';
import { useDropdownList } from 'reactApp/ui/DropdownList/DropdownList.hooks';
import { MenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/MenuButton/MenuButton';
import { getDropdownList } from 'reactApp/ui/ReactViewer/ViewerMenu/MoreMenuButton/MoreMenuButton.helpers';
import type { MenuItem } from 'reactApp/ui/ReactViewer/ViewerMenu/MoreMenuButton/MoreMenuButton.types';
import { sendXray } from 'reactApp/utils/ga';

import styles from './MoreMenuButton.css';

const MoreMenuItem = ({ text, icon }) => {
    const ref = useRef<HTMLDivElement>(null);

    return <DropdownItemAction className={styles.item} text={text} icon={<AnimatedScale target={ref}>{icon}</AnimatedScale>} ref={ref} />;
};

interface Props {
    list: MenuItem[];
    onOpen?: () => void;
    onClose?: () => void;
}

export const MoreMenuButton: FC<Props> = ({ list, onOpen, onClose }) => {
    const { controlRef, isOpen, open, close, findDropdownPosition } = useDropdownList<HTMLDivElement>({ offsetTop: 8 });

    const lottieRef = useRef<LottieRefCurrentProps>(null);

    const dropdownList = useMemo(() => getDropdownList(list, lottieRef), [list, lottieRef]);

    useEffect(() => {
        if (isOpen) {
            onOpen?.();
        } else {
            onClose?.();
        }
    }, [isOpen, onOpen, onClose]);

    return list.length > 0 ? (
        <>
            <MenuButton
                hint="Дополнительные действия"
                icon={<Icon20More />}
                active={isOpen}
                ref={controlRef}
                onClick={() => {
                    sendXray(['viewer-more', 'click']);
                    if (isOpen) {
                        close();
                    } else {
                        open();
                    }
                }}
                qaId="more"
            />
            {isOpen && (
                <DropdownList
                    gaId="viewer-more"
                    className={styles.list}
                    close={close}
                    closeOnResize
                    posX={0}
                    posY={0}
                    parentRef={controlRef}
                    theme={DropdownTheme.space}
                    calcPosition={findDropdownPosition}
                    list={dropdownList}
                    renderItem={(item) => <MoreMenuItem text={item.text} icon={item.icon} />}
                />
            )}
        </>
    ) : null;
};
